import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  conversationControllerGetConversationQueryOptions,
  conversationControllerGetConversationMessagesQueryOptions,
  conversationControllerGetConversationMessagesQueryKey,
  useConversationControllerSendConversationFeedback,
} from 'korint-sdk';
import type { ConversationMessageDto } from 'korint-sdk';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/spinner';
import { Button } from '../../components/ui/button';
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from '../../components/ai-elements/conversation';
import {
  Message,
  MessageContent,
  MessageActions,
  MessageAction,
  MessageResponse,
} from '../../components/ai-elements/message';
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputFooter,
} from '../../components/ai-elements/prompt-input';
import type { PromptInputMessage } from '../../components/ai-elements/prompt-input';
import { Shimmer } from '../../components/ai-elements/shimmer';
import {
  MessageSquareIcon,
  CopyIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  AlertCircleIcon,
  WrenchIcon,
  LoaderIcon,
  CheckIcon,
  RotateCcwIcon,
} from 'lucide-react';
import { BannerConversation } from './BannerConversation';
import type { AIChatAnalytics } from '../FloatingChatModule/useAIChatAnalytics';
import { useConversationStream } from './useConversationStream';
import { getToolDisplayName } from './toolDisplayNames';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';
import { Alert, AlertDescription } from '@folio/components/ui/alert';

interface ActiveToolCall {
  toolName: string;
  state: 'started' | 'completed' | 'error';
}

function ToolCallCard({
  toolName,
  state,
  locale,
}: {
  toolName: string;
  state: 'started' | 'completed' | 'error';
  locale?: string;
}) {
  const { t } = useTranslation();
  return (
    <Group
      gap={2}
      className="rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground mb-2"
    >
      <WrenchIcon className="h-3.5 w-3.5 shrink-0" />
      <Text inline weight="medium">
        {getToolDisplayName(toolName, t, locale)}
      </Text>
      {state === 'started' && <LoaderIcon className="h-3.5 w-3.5 animate-spin ml-auto" />}
      {state === 'completed' && <CheckIcon className="h-3.5 w-3.5 text-green-600 ml-auto" />}
    </Group>
  );
}

export function ConversationThread({
  conversationId,
  initialMessage,
  onInitialMessageSent,
  onNavigate,
  showTitle = true,
  source,
  analytics,
}: {
  conversationId: string;
  initialMessage?: string | null;
  onInitialMessageSent?: () => void;
  onNavigate?: (path: string) => void;
  showTitle?: boolean;
  source?: string;
  analytics?: AIChatAnalytics;
}) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [pendingMessageText, setPendingMessageText] = useState<string | null>(
    initialMessage ?? null,
  );
  const [streamingText, setStreamingText] = useState<string>('');
  const [streamingToolCalls, setStreamingToolCalls] = useState<ActiveToolCall[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorIsTimeout, setErrorIsTimeout] = useState(false);
  const lastSentMessageRef = useRef<string | null>(null);
  const initialMessageSentRef = useRef(false);
  const streamStartTimeRef = useRef<number | null>(null);
  const firstChunkReceivedRef = useRef(false);

  const { streamMessage, abort: abortStream } = useConversationStream();

  const { data: conversation } = useQuery({
    ...conversationControllerGetConversationQueryOptions(conversationId),
  });

  const { data: messagesData, isLoading } = useQuery({
    ...conversationControllerGetConversationMessagesQueryOptions(conversationId),
  });

  const { mutateAsync: sendFeedback } = useConversationControllerSendConversationFeedback({
    mutation: {
      // Patch only the affected message's feedback fields instead of snapshotting the
      // whole messages cache: a snapshot/restore would clobber any messages persisted
      // between onMutate and onError (e.g. the streaming assistant landing mid-flight),
      // which would re-arm the duplicate-bubble window the flicker fix in 1b77f5637a closed.
      onMutate: async ({ messageId, data }) => {
        const queryKey = conversationControllerGetConversationMessagesQueryKey(conversationId);
        await queryClient.cancelQueries({ queryKey });
        const optimisticRating = data?.rating === 1 ? 1 : 0;
        let previousFeedback: { hasFeedback?: boolean; feedbackRating?: 0 | 1 } | undefined;
        queryClient.setQueryData<{ messages: ConversationMessageDto[] }>(queryKey, old => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map(m => {
              if (m.id !== messageId) return m;
              previousFeedback = {
                hasFeedback: m.hasFeedback,
                feedbackRating: m.feedbackRating,
              };
              return { ...m, hasFeedback: true, feedbackRating: optimisticRating };
            }),
          };
        });
        return { previousFeedback };
      },
      onError: (_err, { messageId }, context) => {
        if (!context?.previousFeedback) return;
        const queryKey = conversationControllerGetConversationMessagesQueryKey(conversationId);
        const restore = context.previousFeedback;
        queryClient.setQueryData<{ messages: ConversationMessageDto[] }>(queryKey, old => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map(m => (m.id === messageId ? { ...m, ...restore } : m)),
          };
        });
      },
    },
  });

  const visibleMessages = useMemo(
    () => messagesData?.messages.filter((msg: ConversationMessageDto) => msg.role !== 'SYSTEM'),
    [messagesData?.messages],
  );

  // The optimistic bubbles must not render once the persisted equivalent is in
  // the cache, otherwise the gap between the cache update and the optimistic
  // state being cleared shows a duplicate.
  const showOptimisticUser =
    !!pendingMessageText &&
    !visibleMessages?.some(
      (m: ConversationMessageDto) => m.role === 'USER' && m.content === pendingMessageText,
    );
  const showOptimisticAssistant =
    (!!pendingMessageText || isStreaming) &&
    !(
      streamingMessageId &&
      visibleMessages?.some((m: ConversationMessageDto) => m.id === streamingMessageId)
    );

  const resetStreamingState = useCallback(() => {
    setPendingMessageText(null);
    setStreamingText('');
    setStreamingToolCalls([]);
    setIsStreaming(false);
    setStreamingMessageId(null);
  }, []);

  const doStreamMessage = useCallback(
    async (content: string, isFirstMessage: boolean) => {
      setPendingMessageText(content);
      setStreamingText('');
      setStreamingToolCalls([]);
      setIsStreaming(false);
      setStreamingMessageId(null);
      setErrorMessage(null);
      setErrorIsTimeout(false);
      lastSentMessageRef.current = content;
      streamStartTimeRef.current = Date.now();
      firstChunkReceivedRef.current = false;

      if (analytics && source) {
        analytics.trackStreamStarted?.({
          source,
          conversation_id: conversationId,
        });
      }

      await streamMessage(
        conversationId,
        content,
        {
          onMessageId: (id: string) => {
            setIsStreaming(true);
            setStreamingMessageId(id);
          },
          onTextDelta: (text: string) => {
            if (!firstChunkReceivedRef.current) {
              firstChunkReceivedRef.current = true;
              if (analytics && source) {
                analytics.trackStreamFirstChunk?.({
                  source,
                  conversation_id: conversationId,
                  ttfb_ms: streamStartTimeRef.current
                    ? Date.now() - streamStartTimeRef.current
                    : undefined,
                });
              }
            }
            setStreamingText(prev => prev + text);
          },
          onToolCall: (toolName, state, args) => {
            if (toolName === 'navigate-to' && state === 'started' && args?.path) {
              onNavigate?.(args.path as string);
            }
            setStreamingToolCalls(prev => {
              const existing = prev.findIndex(tc => tc.toolName === toolName);
              if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = { toolName, state };
                return updated;
              }
              return [...prev, { toolName, state }];
            });
          },
          onDone: () => {
            if (analytics && source) {
              analytics.trackStreamCompleted?.({
                source,
                conversation_id: conversationId,
                total_ms: streamStartTimeRef.current
                  ? Date.now() - streamStartTimeRef.current
                  : undefined,
              });
            }
            // Optimistic state is cleared by the effect below, only after the
            // persisted assistant lands in cache — clearing here would leave a
            // 1–2 frame empty slot.
            queryClient.invalidateQueries({
              queryKey: conversationControllerGetConversationMessagesQueryKey(conversationId),
            });
          },
          onError: (error: string, isTimeout?: boolean) => {
            resetStreamingState();
            setErrorIsTimeout(!!isTimeout);
            setErrorMessage(
              isTimeout ? t('conversation.timeoutError') : error || t('conversation.sendError'),
            );
            // Refresh in case the server persisted partial content before failing.
            queryClient.invalidateQueries({
              queryKey: conversationControllerGetConversationMessagesQueryKey(conversationId),
            });
          },
        },
        { ...(source ? { source } : {}), currentPath: pathname },
      );

      if (analytics && source) {
        analytics.trackMessageSent({
          source,
          conversation_id: conversationId,
          message_length: content.length,
          is_first_message: isFirstMessage,
        });
      }
    },
    [conversationId, streamMessage, analytics, source, queryClient, t, pathname],
  );

  // Auto-send initial message when coming from NewConversationForm
  useEffect(() => {
    if (!initialMessage || initialMessageSentRef.current) return;
    initialMessageSentRef.current = true;
    onInitialMessageSent?.();
    doStreamMessage(initialMessage, true);
  }, [initialMessage, onInitialMessageSent, doStreamMessage]);

  useEffect(() => {
    if (!streamingMessageId) return;
    if (visibleMessages?.some((m: ConversationMessageDto) => m.id === streamingMessageId)) {
      resetStreamingState();
    }
  }, [streamingMessageId, visibleMessages, resetStreamingState]);

  const handleSubmit = useCallback(
    async (message: PromptInputMessage) => {
      if (pendingMessageText || isStreaming) return;
      setInput('');
      await doStreamMessage(message.text, !visibleMessages?.length);
    },
    [pendingMessageText, isStreaming, doStreamMessage, visibleMessages?.length],
  );

  // Capture-phase listener on document to intercept internal links for
  // client-side navigation. Must be on document because the Sheet portal
  // renders outside the React component tree.
  useEffect(() => {
    if (!onNavigate) return;

    const handler = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (anchor?.origin === window.location.origin) {
        e.preventDefault();
        onNavigate(anchor.pathname + anchor.search + anchor.hash);
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [onNavigate]);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (anchor && analytics && source) {
        analytics.trackLinkClicked({
          source,
          conversation_id: conversationId,
          link_url: anchor.href,
          link_text: anchor.textContent?.trim() ?? '',
        });
      }
    },
    [analytics, source, conversationId],
  );

  // Clean up stream on real unmount (not StrictMode teardown).
  // StrictMode runs mount → cleanup → re-mount; aborting on the first cleanup
  // kills the in-flight stream before the re-mount can decide to skip (via ref).
  const cleanupCountRef = useRef(0);
  useEffect(() => {
    cleanupCountRef.current = 0;
    return () => {
      cleanupCountRef.current++;
      // In StrictMode, first cleanup (count=1) is fake; second (count=2) is real.
      // In production, there's only one cleanup (count=1) which IS real.
      // Delay the abort to let StrictMode's re-mount happen first.
      const capturedCount = cleanupCountRef.current;
      setTimeout(() => {
        if (cleanupCountRef.current === capturedCount) {
          abortStream();
        }
      }, 50);
    };
  }, [abortStream]);

  if (isLoading && !pendingMessageText && !visibleMessages?.length) {
    return (
      <Center className="py-8">
        <Spinner className="h-6 w-6" />
      </Center>
    );
  }

  return (
    <Stack gap={0} className="h-full">
      {showTitle && (
        <Group className="mb-2 px-4 pt-4">
          <Title order={3} size="sm" truncate className="font-medium">
            {conversation?.title ?? t('conversation.newConversation')}
          </Title>
        </Group>
      )}

      <Conversation>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: event delegation for link tracking */}
        <ConversationContent onClick={handleLinkClick}>
          {visibleMessages?.length === 0 && !pendingMessageText && !isStreaming && !errorMessage ? (
            <ConversationEmptyState
              icon={<MessageSquareIcon className="size-12" />}
              title={t('conversation.newConversation')}
              description={t('conversation.emptyDescription')}
            />
          ) : (
            <>
              {visibleMessages?.map((msg: ConversationMessageDto) => (
                <Message from={msg.role === 'USER' ? 'user' : 'assistant'} key={msg.id}>
                  <MessageContent>
                    {msg.role === 'ASSISTANT' &&
                      (msg as any).toolCalls?.map(
                        (tc: { toolName: string; state: string }, i: number) => (
                          <ToolCallCard
                            key={`${tc.toolName}-${i}`}
                            toolName={tc.toolName}
                            state={tc.state as 'completed' | 'error'}
                            locale={i18n.language}
                          />
                        ),
                      )}
                    <MessageResponse>{msg.content}</MessageResponse>
                  </MessageContent>
                  {msg.role === 'ASSISTANT' && (
                    <MessageActions>
                      {msg.processingTimeMs && (
                        <Text inline size="xs" tone="muted" className="mr-1">
                          {(msg.processingTimeMs / 1000).toFixed(1)}s
                        </Text>
                      )}
                      <MessageAction
                        tooltip={t('conversation.copy')}
                        onClick={() => {
                          navigator.clipboard.writeText(msg.content);
                          if (analytics && source) {
                            analytics.trackResponseCopied({
                              source,
                              conversation_id: conversationId,
                              message_id: msg.id,
                            });
                          }
                        }}
                      >
                        <CopyIcon className="h-4 w-4" />
                      </MessageAction>
                      {(
                        [
                          [1, 'conversation.thumbUp', ThumbsUpIcon],
                          [0, 'conversation.thumbDown', ThumbsDownIcon],
                        ] as const
                      ).map(([rating, labelKey, Icon]) => {
                        const active = msg.feedbackRating === rating;
                        return (
                          <MessageAction
                            key={rating}
                            tooltip={t(active ? 'conversation.feedbackSent' : labelKey)}
                            onClick={() => {
                              if (active) return;
                              sendFeedback({
                                conversationId,
                                messageId: msg.id,
                                data: { rating },
                              });
                              if (analytics && source) {
                                analytics.trackFeedbackGiven({
                                  source,
                                  conversation_id: conversationId,
                                  message_id: msg.id,
                                });
                              }
                            }}
                            disabled={active}
                          >
                            <Icon className="h-4 w-4" fill={active ? 'currentColor' : 'none'} />
                          </MessageAction>
                        );
                      })}
                    </MessageActions>
                  )}
                </Message>
              ))}
              {showOptimisticUser && (
                <Message from="user">
                  <MessageContent>
                    <MessageResponse>{pendingMessageText}</MessageResponse>
                  </MessageContent>
                </Message>
              )}
              {showOptimisticAssistant && (
                <Message from="assistant">
                  <MessageContent>
                    {streamingToolCalls.map((tc, i) => (
                      <ToolCallCard
                        key={`${tc.toolName}-${i}`}
                        toolName={tc.toolName}
                        state={tc.state}
                        locale={i18n.language}
                      />
                    ))}
                    {streamingText ? (
                      <MessageResponse>{streamingText}</MessageResponse>
                    ) : (
                      <Shimmer className="text-sm">{t('conversation.thinking')}</Shimmer>
                    )}
                  </MessageContent>
                </Message>
              )}
              {errorMessage && (
                <Alert variant="destructive">
                  <AlertCircleIcon />
                  <AlertDescription className="flex-1">{errorMessage}</AlertDescription>
                  {errorIsTimeout && lastSentMessageRef.current && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const last = lastSentMessageRef.current;
                        if (last) doStreamMessage(last, !visibleMessages?.length);
                      }}
                    >
                      <RotateCcwIcon className="h-3.5 w-3.5 mr-1.5" />
                      {t('conversation.retry')}
                    </Button>
                  )}
                </Alert>
              )}
            </>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <BannerConversation />
      <PromptInput onSubmit={handleSubmit} className="mx-4 mb-4">
        <PromptInputTextarea
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={t('conversation.inputPlaceholder')}
        />
        <PromptInputFooter>
          <div />
          <PromptInputSubmit disabled={!input.trim() || !!pendingMessageText || isStreaming} />
        </PromptInputFooter>
      </PromptInput>
    </Stack>
  );
}
